import * as _ from "lodash";
import { deleteRequest, get, patch, post } from "../tools/RequestTools";
import { apiURL } from "../Config";
import { CollectionResponse, SimpleResponse } from "../types/ApiResults";
import { DEFAULT_PAGE, DEFAULT_ROWS } from "../domain/constants";
import {
    ActionReducerMapBuilder,
    AsyncThunk,
    Draft,
    GetThunkAPI,
    PayloadAction,
    createAsyncThunk,
} from "@reduxjs/toolkit";
import { BaseModelState, CollectionState } from "../domain/models/BaseModel";
import { PaginationParams } from "../types/PaginationParams";

export class ApiServiceImpl {
    private static getData<T>(obj: any, defaultValue?: T): T {
        return _.get(obj, "data", defaultValue);
    }

    private static getCollectionData<T, CollectionMeta>(
        obj: any,
        defaultValue: CollectionResponse<T, CollectionMeta> = {
            rows: [],
            totalPages: DEFAULT_PAGE,
            totalRows: DEFAULT_ROWS,
        }
    ): CollectionResponse<T> {
        const data = this.getData<CollectionResponse<T, CollectionMeta>>(
            obj,
            defaultValue
        );
        return {
            rows: _.get(data, "rows"),
            totalPages: _.get(data, "totalPages", DEFAULT_PAGE),
            totalRows: _.get(data, "totalRows", DEFAULT_ROWS),
            meta: _.get(data, "meta", {}),
        };
    }

    static async get<T>(
        path: string,
        defaultValue?: any,
        disableLoadingState = false
    ): Promise<T> {
        return this.getData<T>(
            await get(apiURL + path, disableLoadingState),
            defaultValue
        );
    }

    static async getCollection<
        T,
        SortingEnum,
        Params extends PaginationParams<SortingEnum>,
        CollectionMeta = any
    >(
        basePath: string,
        params: Params,
        defaultValue?: CollectionResponse<T, CollectionMeta>,
        disableLoadingState = false
    ): Promise<CollectionResponse<T, CollectionMeta>> {
        const url = Object.keys(params).reduce((acc, key, index) => {
            const value = params[key as keyof Params];
            const prefix = index === 0 ? "?" : "&";
            if (value || _.isBoolean(value)) {
                acc += `${prefix}${key}=${
                    _.isArray(value) ? JSON.stringify(value) : value
                }`;
            }
            return acc;
        }, `${apiURL}${basePath}`);
        return this.getCollectionData<T, CollectionMeta>(
            await get(url, disableLoadingState),
            defaultValue
        );
    }

    static async post<T>(
        path: string,
        data: any,
        defaultValue?: any,
        headers?: HeadersInit,
        stringify = true,
        apiPrefix = ""
    ): Promise<T> {
        return this.getData<T>(
            await post((apiPrefix || apiURL) + path, data, headers, stringify),
            defaultValue
        );
    }

    static async patch<T>(
        path: string,
        data: any,
        defaultValue?: any,
        headers?: HeadersInit,
        stringify = true
    ): Promise<T> {
        return this.getData<T>(
            await patch(apiURL + path, data, "PATCH", headers, stringify),
            defaultValue
        );
    }

    static async delete<T extends SimpleResponse>(path: string): Promise<T> {
        return this.getData<T>(await deleteRequest(apiURL + path));
    }
}

export function createAsyncThunkFactory<Returned, ThunkArg>(
    actionName: string,
    payloadCallback: (
        args: ThunkArg,
        thunkAPI: GetThunkAPI<Returned>
    ) => Promise<Returned>
) {
    return createAsyncThunk<Returned, ThunkArg>(
        actionName,
        async (args, thunkAPI) => {
            try {
                return payloadCallback(args, thunkAPI);
            } catch (error: any) {
                return thunkAPI.rejectWithValue(error.message);
            }
        }
    );
}

export function handleThunk<T>(
    builder: ActionReducerMapBuilder<BaseModelState<T>>,
    thunk: AsyncThunk<T, any, {}>
) {
    builder.addCase(thunk.fulfilled, (state, action: PayloadAction<T>) => {
        state.dto = action.payload as Draft<T>;
    });
}

export function collectionStateCallback<T>(
    state: CollectionState<T>,
    action: any // I tried my best, but redux is an evil that is difficult to typify
) {
    if (
        _.isArray(state.rows) &&
        state.rows?.length &&
        action.meta.arg?.page &&
        action.meta.arg.page !== DEFAULT_PAGE
    ) {
        state.rows = state.rows.concat(action.payload.rows);
    } else {
        state.rows = action.payload.rows;
    }
    state.totalPages = action.payload.totalPages;
    state.totalRows = action.payload.totalRows;
    state.meta = action.payload.meta;
}

export function handleCollectionThunk<T, SortingEnum>(
    builder: ActionReducerMapBuilder<CollectionState<T>>,
    thunk: AsyncThunk<
        CollectionResponse<T>,
        PaginationParams<SortingEnum> | any,
        {}
    >
) {
    builder.addCase(thunk.fulfilled, collectionStateCallback);
}
